import React from 'react';
import PropTypes from 'prop-types';

/**
 * Componente de tabla de datos reutilizable
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.columns - Configuración de columnas [{header, field, width, render, align}]
 * @param {Array} props.data - Datos a mostrar en la tabla
 * @param {Object} props.tableStyles - Estilos para la tabla
 * @param {Object} props.headerStyles - Estilos para el encabezado
 * @param {Object} props.rowStyles - Estilos para las filas
 * @param {Object} props.cellStyles - Estilos para las celdas
 * @param {Function} props.onRowClick - Función a ejecutar al hacer clic en una fila
 * @param {React.RefObject} props.tableRef - Referencia a la tabla
 * @param {string} props.tableId - ID para la tabla
 * @param {boolean} props.showEmpty - Mostrar mensaje cuando no hay datos
 * @param {string} props.emptyMessage - Mensaje a mostrar cuando no hay datos
 * @param {boolean} props.selectable - Permitir selección de filas
 * @param {Function} props.onSelectRow - Función a ejecutar al seleccionar una fila
 * @param {Array} props.selectedRows - IDs de filas seleccionadas
 * @param {string} props.keyField - Campo a usar como key para cada fila
 * @param {number} props.maxHeight - Altura máxima para el cuerpo de la tabla (default: 800px)
 */
const DataTable = ({
  columns = [],
  data = [],
  tableStyles = {},
  headerStyles = {},
  rowStyles = {},
  cellStyles = {},
  onRowClick,
  tableRef,
  tableId = 'data-table',
  showEmpty = true,
  emptyMessage = 'No data available',
  selectable = false,
  onSelectRow,
  selectedRows = [],
  keyField = 'id',
  maxHeight = 500
}) => {
  // Puse esta función para renderizar el contenido de una celda
  const renderCell = (row, column, index) => {
    if (!row) return '';
    
    if (column.render) {
      try {
        return column.render(row[column.field], row, index);
      } catch (error) {
        console.error(`Error rendering cell: ${error.message}`);
        return '-';
      }
    }
    
    return row[column.field] !== undefined ? row[column.field] : '';
  };

  // Esta determina si una fila está seleccionada
  const isRowSelected = (row) => {
    if (!row || row[keyField] === undefined || row[keyField] === null) return false;
    return selectedRows.some(selectedRow => 
      selectedRow && selectedRow[keyField] === row[keyField]
    );
  };

  // Esto maneja la selección de una fila
  const handleRowSelect = (e, row) => {
    e.stopPropagation();
    if (onSelectRow) {
      onSelectRow(row);
    }
  };

  const safeData = Array.isArray(data) ? data.filter(item => item !== null && item !== undefined) : [];

  const containerStyles = {
    overflowX: 'auto',
    width: '100%'
  };

  const tableContainerStyles = {
    display: 'block',
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto',
    width: '100%'
  };

  // Estilos para el thead para que se quede fijo
  const theadStyles = {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: '#f5f5f5'
  };

  return (
    <div className="data-table-container" style={containerStyles}>
      <div className="table-scroll-container" style={tableContainerStyles}>
        <table 
          id={tableId} 
          ref={tableRef}
          style={{ width: '100%', borderCollapse: 'collapse', ...tableStyles }}
        >
          <thead style={theadStyles}>
            <tr style={{ backgroundColor: '#f5f5f5', ...headerStyles }}>
              {selectable && (
                <th style={{ width: '40px', padding: '10px', textAlign: 'center' }}>
                  {/* Si lo necesitan, aquí podría ir un checkbox para seleccionar todas las filas */}
                </th>
              )}
              {columns.map((column, index) => (
                <th 
                  key={index} 
                  style={{ 
                    padding: '10px 15px',
                    textAlign: column.align || 'left',
                    width: column.width || 'auto',
                    fontWeight: 'bold',
                    borderBottom: '1px solid #ddd'
                  }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeData.length > 0 ? (
              safeData.map((row, rowIndex) => (
                <tr 
                  key={row[keyField] || rowIndex}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{ 
                    cursor: onRowClick ? 'pointer' : 'default',
                    backgroundColor: isRowSelected(row) ? '#f2f9ff' : 'white',
                    borderBottom: '1px solid #eee',
                    '&:hover': {
                      backgroundColor: '#f9f9f9'
                    },
                    ...rowStyles
                  }}
                >
                  {selectable && (
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isRowSelected(row)}
                        onChange={(e) => handleRowSelect(e, row)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td 
                      key={colIndex}
                      style={{ 
                        padding: '10px 15px',
                        textAlign: column.align || 'left',
                        ...cellStyles
                      }}
                    >
                      {renderCell(row, column, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))
            ) : showEmpty ? (
              <tr>
                <td 
                  colSpan={selectable ? columns.length + 1 : columns.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      width: PropTypes.string,
      render: PropTypes.func,
      align: PropTypes.oneOf(['left', 'center', 'right'])
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  tableStyles: PropTypes.object,
  headerStyles: PropTypes.object,
  rowStyles: PropTypes.object,
  cellStyles: PropTypes.object,
  onRowClick: PropTypes.func,
  tableRef: PropTypes.object,
  tableId: PropTypes.string,
  showEmpty: PropTypes.bool,
  emptyMessage: PropTypes.string,
  selectable: PropTypes.bool,
  onSelectRow: PropTypes.func,
  selectedRows: PropTypes.array,
  keyField: PropTypes.string
};

export default DataTable;